import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AppUser } from '../boot/session';
import { isDevPreview } from '../boot/devPreview';
import { sb } from '../data/supabase';
import { useUserDirectory } from '../hooks/useUserDirectory';
import { ErrorState } from '../shell/State';

/**
 * 활동 로그 (작업지시서 P4-14 / P5-14 감사 로그 화면)
 *
 * ── 이 화면이 답해야 하는 질문 ──────────────────────────────────────────
 *
 *   "누가 언제 이 도면을 내려받았나"
 *   "이 배포본을 승인한 사람이 누구인가"
 *   "이 공유 링크를 만든 사람이 누구인가"
 *
 *   감사에서 실제로 나오는 질문들이다. 그래서 **동작 종류로 거를 수 있어야**
 *   하고, 사람이 읽을 수 있는 문장으로 보여야 한다.
 *
 * ⚠️ 이 로그는 DB 트리거로 수정·삭제가 막혀 있다(append-only).
 *    화면에서도 편집 수단을 두지 않는다.
 */

interface ActivityRow {
  id: number;
  project_id: string | null;
  drawing_id: string | null;
  version_id: string | null;
  actor: string;
  action: string;
  payload: Record<string, unknown>;
  created_at: string;
}

/** 동작 코드 → 사람이 읽는 말. 모르는 코드는 그대로 보여준다. */
const ACTION_LABEL: Record<string, string> = {
  'drawing.open': '도면 열람',
  'drawing.create': '도면 생성',
  'version.create': '버전 생성',
  'version.restore': '버전 되돌리기',
  'version.status': '상태 변경',
  'version.request': '검토 요청',
  'version.approve': '승인',
  'version.reject': '반려',
  'version.changes_requested': '수정 요청',
  'version.publish': '현장 배포',
  'version.archive': '보관',
  'branch.create': '분기 생성',
  'comment.create': '댓글 작성',
  'upload.create': '파일 업로드',
  'export.create': '내보내기',
  'share.create': '공유 링크 생성',
  'user.update': '사용자 정보 변경',
  'block.edit_proposed': '블록 수정 제안',
  'block.edit_approved': '블록 수정 승인',
  'block.edit_rejected': '블록 수정 반려',
  'project.delete': '프로젝트 삭제',
  'drawing.delete': '도면 삭제',
  'base_drawing.create': '기본 도면 등록',
  'base_drawing.update': '기본 도면 수정',
  'base_drawing.delete': '기본 도면 삭제',
};

/** 감사에서 중요한 동작 — 기본 필터에 쓴다 */
const AUDIT_ACTIONS = [
  'version.approve',
  'version.publish',
  'version.restore',
  'export.create',
  'share.create',
];

type Filter = 'all' | 'audit' | 'mine';

export function ActivityPage({ user }: { user: AppUser }) {
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const { nameOf } = useUserDirectory();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('audit');
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (isDevPreview()) {
        setRows(devRows(user.email));
        return;
      }
      const logResult = await sb()
        .from('cad_activity_log')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);
      if (logResult.error) throw new Error(logResult.error.message);
      setRows((logResult.data ?? []) as ActivityRow[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [user.email]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === 'audit' && !AUDIT_ACTIONS.includes(r.action)) return false;
      if (filter === 'mine' && r.actor !== user.email) return false;
      if (!q) return true;
      return (
        r.actor.toLowerCase().includes(q) ||
        r.action.toLowerCase().includes(q) ||
        JSON.stringify(r.payload).toLowerCase().includes(q)
      );
    });
  }, [rows, filter, query, user.email]);

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h1 className="page__title">활동 로그</h1>
          <p className="page__desc">
            최근 500건. 이 기록은 수정하거나 지울 수 없습니다.
          </p>
        </div>
        <span className="badge">P4-14</span>
      </header>

      {error && (
        <ErrorState title="활동 로그를 불러올 수 없습니다" message={error} onRetry={load} />
      )}

      <div className="card">
        <div className="toolbar">
          <input
            className="input input--search"
            type="search"
            placeholder="사람 · 동작 · 내용 검색"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="seg" role="tablist">
            {(
              [
                ['audit', '주요 동작'],
                ['mine', '내 활동'],
                ['all', '전체'],
              ] as const
            ).map(([f, label]) => (
              <button
                key={f}
                type="button"
                role="tab"
                aria-selected={filter === f}
                className="seg__btn"
                onClick={() => setFilter(f)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="muted">불러오는 중…</p>
        ) : (
          <div className="table-wrap">
            <table className="table table--compact">
              <thead>
                <tr>
                  <th>시각</th>
                  <th>사람</th>
                  <th>동작</th>
                  <th>대상</th>
                  <th>내용</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{new Date(r.created_at).toLocaleString('ko-KR')}</td>
                    <td title={r.actor}>{nameOf(r.actor)}</td>
                    <td>{ACTION_LABEL[r.action] ?? r.action}</td>
                    <td className="mono muted">{targetOf(r)}</td>
                    <td className="muted">{summarize(r.payload)}</td>
                  </tr>
                ))}
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted">
                      {filter === 'audit'
                        ? '주요 동작 기록이 없습니다. 전체를 보려면 필터를 바꾸세요.'
                        : '조건에 맞는 기록이 없습니다.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function targetOf(r: ActivityRow): string {
  if (r.version_id) return `버전 ${r.version_id.slice(0, 8)}`;
  if (r.drawing_id) return `도면 ${r.drawing_id.slice(0, 8)}`;
  if (r.project_id) return `프로젝트 ${r.project_id.slice(0, 8)}`;
  return '—';
}

/** payload 를 한 줄로. 원문 JSON 을 그대로 보여주면 아무도 안 읽는다. */
function summarize(payload: Record<string, unknown>): string {
  const bits: string[] = [];
  if (typeof payload.from === 'string' && typeof payload.to === 'string') {
    bits.push(`${payload.from} → ${payload.to}`);
  } else if (typeof payload.from === 'string') {
    // 되돌리기는 출처만 남는다 — 키 이름('from')이 그대로 보이면 안 된다
    bits.push(`${payload.from} 에서`);
  }
  if (typeof payload.label === 'string') bits.push(payload.label);
  if (typeof payload.branch === 'string' && typeof payload.name !== 'string') {
    bits.push(`분기 ${payload.branch.slice(0, 8)}`);
  }
  if (typeof payload.reason === 'string' && payload.reason) bits.push(payload.reason);
  if (typeof payload.name === 'string') bits.push(payload.name);
  if (typeof payload.filename === 'string') bits.push(payload.filename);
  if (Array.isArray(payload.mentions) && payload.mentions.length > 0) {
    bits.push(`멘션 ${payload.mentions.length}명`);
  }

  if (bits.length > 0) return bits.join(' · ');

  const keys = Object.keys(payload);
  return keys.length > 0 ? keys.slice(0, 3).join(', ') : '—';
}

/* -------------------------------------------------------------------------- */

function devRows(me: string): ActivityRow[] {
  const now = Date.now();
  const mk = (
    id: number,
    minutes: number,
    actor: string,
    action: string,
    payload: Record<string, unknown> = {}
  ): ActivityRow => ({
    id,
    project_id: 'p-0001',
    drawing_id: 'd-1001',
    version_id: 'v-dev',
    actor,
    action,
    payload,
    created_at: new Date(now - minutes * 60_000).toISOString(),
  });

  return [
    mk(9, 3, me, 'version.publish', { from: 'approved', to: 'published' }),
    mk(8, 12, me, 'version.approve', { from: 'in_review', to: 'approved' }),
    mk(7, 45, 'designer.demo@trial-net.co.kr', 'version.changes_requested', {
      from: 'in_review',
      to: 'changes_requested',
      reason: '신선 코너 통로 폭 재검토 필요',
    }),
    mk(6, 60, 'designer.demo@trial-net.co.kr', 'export.create', { filename: '수원점_1F_A안.pdf' }),
    mk(5, 95, 'store.demo@trial-net.co.kr', 'comment.create', { mentions: ['reviewer.demo@trial-net.co.kr'] }),
    mk(4, 140, 'designer.demo@trial-net.co.kr', 'version.restore', { from: 'v-dev-0' }),
    mk(3, 180, 'designer.demo@trial-net.co.kr', 'branch.create', { name: 'B안 — 신선 확장' }),
    mk(2, 240, 'designer.demo@trial-net.co.kr', 'upload.create', { filename: '수원점_현황.dwg' }),
    mk(1, 300, me, 'drawing.open', {}),
  ];
}
